/**
 * BoundedContextImpl - Core implementation of BoundedContext interface.
 *
 * This implementation provides the core behavior of a Bounded Context:
 * - Identity management (id, localId, domainId, uri, name, definition)
 * - Rigid domain relationship (existential dependency)
 * - Model and UbiquitousLanguage management
 * - View and Dataset reference management
 *
 * This is located in semantical-core and has NO dependencies on:
 * - KnowledgeGraph module
 * - Diagram module
 * - Ontology loading/parsing
 *
 * For ontology operations and KnowledgeGraph management, use the services
 * in the workspace module (OntologyService, KnowledgeGraphService).
 */
import { ModelImpl } from './Model';
import { UbiquitousLanguageImpl } from './UbiquitousLanguage';
/**
 * BoundedContextImpl - Core bounded context implementation.
 *
 * Implements the BoundedContext interface with core behavior only.
 * For heavy operations (ontology loading, KG management), use external services.
 */
export class BoundedContextImpl {
    /**
     * Create a new BoundedContextImpl instance.
     *
     * @param options - Configuration options for the bounded context
     */
    constructor(options) {
        // ==================== Identity ====================
        /**
         * Surrogate identifier for the bounded context.
         * For convenience, but the composite key (domainId, localId) is authoritative.
         */
        Object.defineProperty(this, "id", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        /**
         * Local identifier within the domain.
         * Combined with domainId forms the composite logical identifier.
         */
        Object.defineProperty(this, "localId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        /**
         * Domain identifier - rigid/immutable, established at creation.
         * Part of the composite logical identifier (domainId, localId).
         */
        Object.defineProperty(this, "domainId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        /** URI identifier for the bounded context */
        Object.defineProperty(this, "uri", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        /** Display name for the bounded context */
        Object.defineProperty(this, "name", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        /** Natural language definition of this context's purpose */
        Object.defineProperty(this, "definition", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        // ==================== Core Relationships ====================
        /**
         * The domain this bounded context belongs to.
         * This relationship is rigid - once established, it cannot change.
         */
        Object.defineProperty(this, "_domain", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        /** The contextual model for this bounded context */
        Object.defineProperty(this, "_model", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        /** The Ubiquitous Language for this bounded context */
        Object.defineProperty(this, "_ubiquitousLanguage", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        // ==================== Views and Datasets ====================
        /**
         * Ordered array of views owned by this bounded context.
         * The array order is authoritative - views are displayed in this order.
         */
        Object.defineProperty(this, "_views", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        /**
         * Raw datasets owned by this bounded context.
         * These are data sources that can be bound to produce knowledge graphs.
         */
        Object.defineProperty(this, "_datasets", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        /**
         * KG bindings owned by this bounded context.
         * A binding combines a Dataset with a GraphMapping to produce KG triples.
         */
        Object.defineProperty(this, "_kgBindings", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        /**
         * Constraint bindings owned by this bounded context.
         * A binding wraps a Dataset with SHACL triples and produces Shapes.
         */
        Object.defineProperty(this, "_constraintBindings", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        /**
         * Knowledge graphs owned by this bounded context.
         * These contain ABox (assertional) data - individuals and relationships.
         */
        Object.defineProperty(this, "_knowledgeGraphs", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        // Domain is required - establishes existential dependency
        if (!options.domain) {
            throw new Error('BoundedContextImpl requires a domain');
        }
        // Establish rigid/existential domain dependency first
        this._domain = options.domain;
        this.domainId = options.domain.id;
        // Initialize identity
        const counter = ++BoundedContextImpl.boundedContextCounter;
        this.localId = options.localId ?? options.id ?? `context-${counter}`;
        this.id = options.id ?? this.localId;
        this.uri = `urn:semantical:context:${this.id}`;
        this.name = options.name ?? `Bounded Context ${counter}`;
        this.definition = options.definition;
        // Register with domain
        this._domain.addBoundedContext(this);
        // Initialize or use provided model
        if (options.model) {
            this._model = options.model;
        }
        else {
            this._model = new ModelImpl({
                id: `${this.id}-model`,
                name: `${this.name} Model`
            });
        }
        // Initialize or use provided ubiquitous language
        if (options.ubiquitousLanguage) {
            this._ubiquitousLanguage = options.ubiquitousLanguage;
        }
        else {
            this._ubiquitousLanguage = new UbiquitousLanguageImpl({
                id: `${this.id}-ul`,
                name: `${this.name} Language`
            });
        }
    }
    // ==================== BoundedContext Interface ====================
    /**
     * Get the domain this bounded context belongs to.
     */
    getDomain() {
        return this._domain;
    }
    /**
     * Get the contextual model for this bounded context.
     */
    getModel() {
        return this._model;
    }
    /**
     * Get the Ubiquitous Language for this bounded context.
     */
    getUbiquitousLanguage() {
        return this._ubiquitousLanguage;
    }
    /**
     * Get all views owned by this bounded context.
     * The array order is authoritative - views are displayed in this order.
     */
    getViews() {
        return this._views;
    }
    /**
     * Get all raw datasets owned by this bounded context.
     */
    getDatasets() {
        return this._datasets;
    }
    /**
     * Get all KG bindings owned by this bounded context.
     */
    getKGBindings() {
        return this._kgBindings;
    }
    /**
     * Get all constraint bindings owned by this bounded context.
     */
    getConstraintBindings() {
        return this._constraintBindings;
    }
    /**
     * Get all knowledge graphs owned by this bounded context.
     */
    getKnowledgeGraphs() {
        return this._knowledgeGraphs;
    }
    // ==================== Vocabulary Management ====================
    /**
     * Get all vocabulary bindings from the Ubiquitous Language.
     */
    getVocabularyBindings() {
        return this._ubiquitousLanguage.getVocabularyBindings();
    }
    /**
     * Add a vocabulary binding to the Ubiquitous Language.
     */
    addVocabularyBinding(binding) {
        this._ubiquitousLanguage.addVocabularyBinding(binding);
    }
    // ==================== View Management ====================
    /**
     * Get a view by ID.
     */
    getView(viewId) {
        return this._views.find(v => v.id === viewId);
    }
    /**
     * Register a view in the bounded context.
     * If a view with the same ID exists, it is replaced in the same position.
     * New views are appended to the end.
     *
     * @param view - The View instance to register
     */
    registerView(view) {
        const existingIndex = this._views.findIndex(v => v.id === view.id);
        if (existingIndex >= 0) {
            this._views[existingIndex] = view;
        }
        else {
            this._views.push(view);
        }
    }
    /**
     * Check if a view with the given ID exists.
     */
    hasView(viewId) {
        return this._views.some(v => v.id === viewId);
    }
    /**
     * Remove a view by ID.
     */
    removeView(viewId) {
        const index = this._views.findIndex(v => v.id === viewId);
        if (index >= 0) {
            this._views.splice(index, 1);
            return true;
        }
        return false;
    }
    /**
     * Clear all views.
     */
    clearViews() {
        this._views.length = 0;
    }
    /**
     * Get all view IDs in order.
     */
    getAllViewIds() {
        return this._views.map(v => v.id);
    }
    /**
     * Move a view to a new position in the list.
     */
    moveView(viewId, newIndex) {
        const currentIndex = this._views.findIndex(v => v.id === viewId);
        if (currentIndex < 0)
            return false;
        const [view] = this._views.splice(currentIndex, 1);
        const targetIndex = Math.max(0, Math.min(newIndex, this._views.length));
        this._views.splice(targetIndex, 0, view);
        return true;
    }
    // ==================== Dataset Management ====================
    /**
     * Add or update a dataset.
     */
    addDataset(dataset) {
        const existingIndex = this._datasets.findIndex(d => d.id === dataset.id);
        if (existingIndex >= 0) {
            this._datasets[existingIndex] = dataset;
        }
        else {
            this._datasets.push(dataset);
        }
    }
    /**
     * Get a dataset by ID.
     */
    getDataset(datasetId) {
        return this._datasets.find(d => d.id === datasetId);
    }
    /**
     * Check if a dataset with the given ID exists.
     */
    hasDataset(datasetId) {
        return this._datasets.some(d => d.id === datasetId);
    }
    /**
     * Remove a dataset by ID.
     */
    removeDataset(datasetId) {
        const index = this._datasets.findIndex(d => d.id === datasetId);
        if (index >= 0) {
            this._datasets.splice(index, 1);
            return true;
        }
        return false;
    }
    /**
     * Clear all datasets.
     */
    clearDatasets() {
        this._datasets.length = 0;
    }
    // ==================== KGBinding Management ====================
    /**
     * Add or update a KG binding.
     */
    addKGBinding(binding) {
        const existingIndex = this._kgBindings.findIndex(b => b.id === binding.id);
        if (existingIndex >= 0) {
            this._kgBindings[existingIndex] = binding;
        }
        else {
            this._kgBindings.push(binding);
        }
    }
    /**
     * Get a KG binding by ID.
     */
    getKGBinding(bindingId) {
        return this._kgBindings.find(b => b.id === bindingId);
    }
    /**
     * Check if a KG binding with the given ID exists.
     */
    hasKGBinding(bindingId) {
        return this._kgBindings.some(b => b.id === bindingId);
    }
    /**
     * Remove a KG binding by ID.
     */
    removeKGBinding(bindingId) {
        const index = this._kgBindings.findIndex(b => b.id === bindingId);
        if (index >= 0) {
            this._kgBindings.splice(index, 1);
            return true;
        }
        return false;
    }
    /**
     * Clear all KG bindings.
     */
    clearKGBindings() {
        this._kgBindings.length = 0;
    }
    // ==================== ConstraintBinding Management ====================
    /**
     * Add or update a constraint binding.
     */
    addConstraintBinding(binding) {
        const existingIndex = this._constraintBindings.findIndex(b => b.id === binding.id);
        if (existingIndex >= 0) {
            this._constraintBindings[existingIndex] = binding;
        }
        else {
            this._constraintBindings.push(binding);
        }
    }
    /**
     * Get a constraint binding by ID.
     */
    getConstraintBinding(bindingId) {
        return this._constraintBindings.find(b => b.id === bindingId);
    }
    /**
     * Check if a constraint binding with the given ID exists.
     */
    hasConstraintBinding(bindingId) {
        return this._constraintBindings.some(b => b.id === bindingId);
    }
    /**
     * Remove a constraint binding by ID.
     */
    removeConstraintBinding(bindingId) {
        const index = this._constraintBindings.findIndex(b => b.id === bindingId);
        if (index >= 0) {
            this._constraintBindings.splice(index, 1);
            return true;
        }
        return false;
    }
    /**
     * Clear all constraint bindings.
     */
    clearConstraintBindings() {
        this._constraintBindings.length = 0;
    }
    // ==================== KnowledgeGraph Management ====================
    /**
     * Add or update a knowledge graph.
     */
    addKnowledgeGraph(kg) {
        const existingIndex = this._knowledgeGraphs.findIndex(k => k.id === kg.id);
        if (existingIndex >= 0) {
            this._knowledgeGraphs[existingIndex] = kg;
        }
        else {
            this._knowledgeGraphs.push(kg);
        }
    }
    /**
     * Get a knowledge graph by ID.
     */
    getKnowledgeGraph(kgId) {
        return this._knowledgeGraphs.find(k => k.id === kgId);
    }
    /**
     * Check if a knowledge graph with the given ID exists.
     */
    hasKnowledgeGraph(kgId) {
        return this._knowledgeGraphs.some(k => k.id === kgId);
    }
    /**
     * Remove a knowledge graph by ID.
     */
    removeKnowledgeGraph(kgId) {
        const index = this._knowledgeGraphs.findIndex(k => k.id === kgId);
        if (index >= 0) {
            this._knowledgeGraphs.splice(index, 1);
            return true;
        }
        return false;
    }
    /**
     * Clear all knowledge graphs.
     */
    clearKnowledgeGraphs() {
        this._knowledgeGraphs.length = 0;
    }
    // ==================== Serialization ====================
    /**
     * Serialize to JSON-LD with per-object @context.
     */
    toJSON(options) {
        // Build @context with set container aliases for arrays
        const context = {
            '@vocab': 'http://semantical.io/ontology/core#'
        };
        if (options?.includeDiagrams) {
            context.diagrams = { '@id': 'hasDiagram', '@type': '@id', '@container': '@set' };
        }
        if (options?.includeDatasets) {
            context.datasets = { '@id': 'hasDataset', '@type': '@id', '@container': '@set' };
        }
        const result = {
            '@context': context,
            '@type': 'BoundedContext',
            '@id': this.uri,
            name: this.name,
            hasModel: this._model.toJSON()
        };
        if (this.definition) {
            result.definition = this.definition;
        }
        // Only include UbiquitousLanguage if it has vocabularies
        const ul = this._ubiquitousLanguage;
        if (ul.getVocabularyBindings().length > 0) {
            result.hasUbiquitousLanguage = ul.toJSON();
        }
        // Include views (diagrams) if requested
        if (options?.includeDiagrams && this._views.length > 0) {
            result.diagrams = this._views.map(v => 'toJSON' in v && typeof v.toJSON === 'function'
                ? v.toJSON()
                : v);
        }
        // Include KG bindings if requested (UI displays these)
        if (options?.includeDatasets && this._kgBindings.length > 0) {
            result.datasets = this._kgBindings.map(binding => ({
                '@type': 'DatasetBinding',
                '@id': `urn:semantical:dataset-binding:${binding.id}`,
                name: binding.name,
                namespace: binding.namespace,
                ontologyIri: binding.ontologyIri
            }));
        }
        return result;
    }
}
/** Counter for generating unique bounded context IDs */
Object.defineProperty(BoundedContextImpl, "boundedContextCounter", {
    enumerable: true,
    configurable: true,
    writable: true,
    value: 0
});
